import {combineReducers} from 'redux';
import {connectRouter, RouterState} from 'connected-react-router';
import {History} from 'history';

import blockReducer, {BlockState, initialBlockState} from './blockReducer';
import blockResultReducer, {IBlockResultState, initialBlockResultState} from './blockResultReducer';
import viewsReducer, {IViewState, initialViewState} from './viewReducer';
import webhookReducer, {IWebhookState, initialWebhookState} from './webhookReducer';
import workflowsReducer, {IWorkflowState, initialWorkflowState} from './workflowReducer';

import noteflowReducer, {
  INoteflowState,
  initialNoteflowState,
} from './noteflowReducer';
import noteflowTaskReducer, {
  INoteflowTaskState,
  initialNoteflowTaskState,
} from './noteflowTaskReducer';
import databasesReducer, {IDatabaseState, initialDatabaseState} from './databaseReducer';
import dataTreeReducer, {IDataTreeState, initialDataTreeState} from './dataTreeReducer';
import viewTreeReducer, {IViewTreeState, initialViewTreeState} from './viewTreeReducer';
import schemasReducer, {ISchemaState, initialSchemaState} from './schemaReducer';
import tablesReducer, {ITableState, initialTableState} from './tableReducer';
import tableauReducer, {ITableauState, initialTableauState} from './tableauReducer';
import usersReducer, {IUserState, initialUserState} from './userReducer';
import rolesReducer, {IRoleState, initialRoleState} from './roleReducer';
import organizationsReducer, {IOrganizationState, initialOrganizationState} from './organizationReducer';
import workspacesReducer, {IWorkspaceState, initialWorkspaceState} from "./workspaceReducer";
import uiReducer, {IUiState, initialUiState} from './uiReducer';
import searchReducer, {ISearchState, initialSearchState} from './searchReducer';
import sharingSettingsReducer, {initialSharingSettingsState, ISharingSettingsState} from './sharingSettingsReducer';
import subscriptionReducer, {initialSubscriptionState, ISubscriptionState} from './subscriptionReducer';
import tagReducer, {ITagState, initialTagState} from './tagReducer';
import activitiesReducer, {IActivitiesState, initialActivitiesState} from './activityReducer';
import editorReducer, {IEditorState, initialEditorState} from './editorReducer';
import unsplashReducer, {initialUnsplashState, IUnsplashState} from './unsplashReducer';
import favoriteReducer, {IFavoriteState, initialFavoriteState} from './favoriteReducer';
import folderReducer, {FolderState, initialFolderState} from './folderReducer';
import integrationReducer, {IIntegrationState, initialIntegrationState} from './integrationReducer';
import s3ConnectionReducer, {S3ConnectionState, initialS3ConnectionState} from './s3ConnectionReducer';
import reportReducer, {ReportState, initialReportState} from './reportReducer';
import reportResultReducer, {ReportResultState, initialReportResultState} from './reportResultReducer';
import reportFormReducer, {ReportFormState, initialReportFormState} from './reportFormReducer';
import savedBlockReducer, {ISavedBlockState, initialSavedBlockState} from './savedBlockReducer';
import dataRetentionReducer, {DataRetentionState, initialDataRetentionState} from "./dataRetentionReducer";
import codeBlockReducer, {ICodeBlockState, initialCodeBlockState} from "./codeBlocksReducer";

export interface IState {
  router?: RouterState;
  block: BlockState;
  blockResult: IBlockResultState;
  views: IViewState;
  webhook: IWebhookState;
  workflows: IWorkflowState;
  noteflow: INoteflowState;
  noteflowTask: INoteflowTaskState;
  databases: IDatabaseState;
  dataTree: IDataTreeState;
  viewTree: IViewTreeState;
  schemas: ISchemaState;
  tables: ITableState;
  tableau: ITableauState;
  users: IUserState;
  roles: IRoleState;
  organizations: IOrganizationState;
  workspaces: IWorkspaceState;
  ui: IUiState;
  search: ISearchState;
  sharingSettings: ISharingSettingsState;
  subscription: ISubscriptionState;
  tags: ITagState;
  activities: IActivitiesState;
  editor: IEditorState; // SQL Editor (autocomplete, metadata)
  unsplash: IUnsplashState;
  favorite: IFavoriteState;
  savedBlock: ISavedBlockState;
  folder: FolderState;
  integration: IIntegrationState;
  s3Connection: S3ConnectionState;
  report: ReportState;
  reportForm: ReportFormState;
  reportResult: ReportResultState;
  dataRetention: DataRetentionState;
  codeBlock: ICodeBlockState;
}

export const initialState: IState = {
  views: initialViewState,
  block: initialBlockState,
  blockResult: initialBlockResultState,
  webhook: initialWebhookState,
  workflows: initialWorkflowState,
  noteflow: initialNoteflowState,
  noteflowTask: initialNoteflowTaskState,
  databases: initialDatabaseState,
  dataTree: initialDataTreeState,
  viewTree: initialViewTreeState,
  schemas: initialSchemaState,
  tables: initialTableState,
  tableau: initialTableauState,
  users: initialUserState,
  roles: initialRoleState,
  organizations: initialOrganizationState,
  workspaces: initialWorkspaceState,
  ui: initialUiState,
  search: initialSearchState,
  sharingSettings: initialSharingSettingsState,
  subscription: initialSubscriptionState,
  tags: initialTagState,
  activities: initialActivitiesState,
  editor: initialEditorState,
  unsplash: initialUnsplashState,
  favorite: initialFavoriteState,
  savedBlock: initialSavedBlockState,
  folder: initialFolderState,
  integration: initialIntegrationState,
  s3Connection: initialS3ConnectionState,
  report: initialReportState,
  reportForm: initialReportFormState,
  reportResult: initialReportResultState,
  dataRetention: initialDataRetentionState,
  codeBlock: initialCodeBlockState,
};

export default (history: History) => combineReducers({
  router: connectRouter(history),
  block: blockReducer,
  blockResult: blockResultReducer,
  views: viewsReducer,
  webhook: webhookReducer,
  workflows: workflowsReducer,
  noteflow: noteflowReducer,
  noteflowTask: noteflowTaskReducer,
  databases: databasesReducer,
  dataTree: dataTreeReducer,
  viewTree: viewTreeReducer,
  schemas: schemasReducer,
  tables: tablesReducer,
  tableau: tableauReducer,
  users: usersReducer,
  roles: rolesReducer,
  organizations: organizationsReducer,
  workspaces: workspacesReducer,
  ui: uiReducer,
  search: searchReducer,
  sharingSettings: sharingSettingsReducer,
  subscription: subscriptionReducer,
  tags: tagReducer,
  activities: activitiesReducer,
  editor: editorReducer,
  unsplash: unsplashReducer,
  favorite: favoriteReducer,
  savedBlock: savedBlockReducer,
  folder: folderReducer,
  integration: integrationReducer,
  s3Connection: s3ConnectionReducer,
  report: reportReducer,
  reportForm: reportFormReducer,
  reportResult: reportResultReducer,
  dataRetention: dataRetentionReducer,
  codeBlock: codeBlockReducer,
});
